import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import {
  CaptureFilter,
  COLUMN_SETTINGS,
  ColumnSetting,
  CtPreference,
  PROJECT_PREFIX,
  sanitizeAndFormatDate,
} from '@ct/model';
import { of } from 'rxjs';
import { skipErrorDialog } from '../interceptors/error.interceptor';
import { RouteConfigService } from './route-config.service';

@Injectable({ providedIn: 'root' })
export class PreferencesService {
  private readonly http = inject(HttpClient);
  private readonly msal = inject(MsalService);
  private readonly injector = inject(Injector);
  private readonly routeConfig = inject(RouteConfigService);

  headers = new HttpHeaders().set('x-ms-client-principal-name', 'xxx');
  private _ctPath: string | null = null;

  private get proj(): string {
    // Try route config first, then fallback to injector
    const config = this.routeConfig.config();
    if (config?.projectPrefix) {
      return config.projectPrefix;
    }
    return this.injector.get(PROJECT_PREFIX, 'default');
  }

  private get columnSettings(): ColumnSetting[] {
    // Try route config first, then fallback to injector
    const config = this.routeConfig.config();
    if (config?.columnSettings) {
      return config.columnSettings;
    }
    return this.injector.get(COLUMN_SETTINGS, []);
  }

  private get ctPath(): string {
    if (this._ctPath === null) {
      const proj = this.proj;
      this._ctPath = proj ? proj + 'ct' : 'ct';
    }
    return this._ctPath;
  }

  getColumnSettings() {
    const stored = localStorage.getItem('columnSettings');
    let columnSettings: ColumnSetting[];
    if (stored) {
      const savedColumnSettings = JSON.parse(stored) as ColumnSetting[];
      columnSettings = this.columnSettings.map((col) => {
        const savedColumn = savedColumnSettings.find((c) => c.prop === col.prop);
        if (savedColumn) {
          return { ...savedColumn, name: col.name };
        } else {
          return { name: col.name, prop: col.prop, visible: true, index: 100 };
        }
      });
    } else {
      columnSettings = [...this.columnSettings];
    }
    return of(columnSettings.sort((a, b) => a.index - b.index));
  }

  getFilterPreferences() {
    const activeAccount = this.msal.instance.getActiveAccount();
    if (!activeAccount) {
      return of([]);
    }
    const username = activeAccount.username;
    let params = new HttpParams();
    params = params.append('user', username);
    return this.http.get<CtPreference[]>(`/${this.ctPath}/userconfig/userqueries`, {
      params,
      headers: this.headers,
      context: skipErrorDialog(), // No need to display warning dialog for this.
    });
  }

  deleteFilterPreference(preference: CtPreference) {
    const cleanedPreference = sanitizePreference(preference);
    return this.http.request('DELETE', `/${this.ctPath}/userconfig/deleteuserquery`, {
      body: cleanedPreference,
      headers: this.headers,
    });
  }

  editFilterPreference(preference: CtPreference) {
    const cleanedPreference = sanitizePreference(preference);
    return this.http.put<CtPreference>(`/${this.ctPath}/userconfig/userquery`, cleanedPreference, {
      headers: this.headers,
    });
  }

  setColumnSettings(columnSettings: ColumnSetting[]) {
    localStorage.setItem('columnSettings', JSON.stringify(columnSettings));
  }

  setFilterPreferences(filterPreferences) {
    localStorage.setItem('lngFilterPreferences', JSON.stringify(filterPreferences));
  }
}

export const sanitizePreference = (preference: CtPreference) => {
  const captureFilter: CaptureFilter = {
    ...preference.payload.captureFilter,
    bldate: {
      start: sanitizeAndFormatDate(preference.payload.captureFilter?.bldate?.start),
      end: sanitizeAndFormatDate(preference.payload.captureFilter?.bldate?.end),
    },
    dateDestination: {
      start: sanitizeAndFormatDate(preference.payload.captureFilter?.dateDestination?.start),
      end: sanitizeAndFormatDate(preference.payload.captureFilter?.dateDestination?.end),
    },
  };
  const cleanedPreference: CtPreference = {
    ...preference,
    payload: {
      ...preference.payload,
      captureFilter,
    },
    preSelected: preference.default,
  };
  delete cleanedPreference.default; // Not possible to use deconstructor on attribute "default"
  // Backend DTO uses FAIL_ON_UNKNOWN_PROPERTIES — must not send `external`
  // (set by @ngx-stoui/common's sto-preference UI) or the request is rejected
  // with HTTP 400 "Failed to read request".
  delete cleanedPreference.external;

  return cleanedPreference;
};
